import { Streams } from '@cookspace-shared-contracts/core';

import { baseApi } from '@/api/baseApi';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /streams/* и /chat-messages/*.
 * Read-only режим: list (всех статусов) и get details.
 * Дополнительно: удаление сообщения чата стрима (модерация).
 * admin-backend на порту 5002 без префикса /admin.
 */
export const streamsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listStreams: build.query<
            Streams.GetStreamsResponse,
            Streams.GetStreamsRequest | void
        >({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.search) {
                    search.search = params.search;
                }
                if (params?.categoryId) {
                    search.categoryId = params.categoryId;
                }
                if (params?.status) {
                    search.status = params.status;
                }
                if (params?.isPremium !== undefined) {
                    search.isPremium = params.isPremium ? 'true' : 'false';
                }
                if (params?.isFree !== undefined) {
                    search.isFree = params.isFree ? 'true' : 'false';
                }
                if (params?.isUserContent !== undefined) {
                    search.isUserContent = params.isUserContent ? 'true' : 'false';
                }
                if (params?.authorId) {
                    search.authorId = params.authorId;
                }
                if (params?.scheduledAfter) {
                    search.scheduledAfter = params.scheduledAfter;
                }
                if (params?.scheduledBefore) {
                    search.scheduledBefore = params.scheduledBefore;
                }
                if (params?.limit !== undefined) {
                    search.limit = String(params.limit);
                }
                if (params?.offset !== undefined) {
                    search.offset = String(params.offset);
                }
                return {
                    url: '/streams',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): Streams.GetStreamsResponse =>
                parseWithSchema(Streams.GetStreamsResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((s) => ({
                            type: 'Stream' as const,
                            id: s.id
                        })),
                        { type: 'Stream' as const, id: 'LIST' }
                    ]
                    : [{ type: 'Stream' as const, id: 'LIST' }]
        }),

        getStream: build.query<Streams.GetStreamResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/streams/${id}`,
                method: 'GET'
            }),
            transformResponse: (raw: unknown): Streams.GetStreamResponse =>
                parseWithSchema(Streams.GetStreamResponseSchema, raw),
            providesTags: (_result, _error, arg) => [{ type: 'Stream', id: arg.id }]
        }),

        deleteChatMessage: build.mutation<Record<string, never>, { id: string }>({
            query: ({ id }) => ({
                url: `/chat-messages/${id}`,
                method: 'DELETE'
            })
        })
    })
});

export const {
    useListStreamsQuery,
    useGetStreamQuery,
    useDeleteChatMessageMutation
} = streamsApi;
